<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next';
import { RouterLink } from 'vue-router';

import BrandLogo from '#/components/layout/BrandLogo.vue';
import FooterLinkList from '#/components/layout/FooterLinkList.vue';
import { brand } from '#/config/brand';
import { FOOTER_COLUMNS, FOOTER_LEGAL_LINKS } from '#/config/footerLinks';

/**
 * 전역 푸터(AppFooter) — 2026-10 커머스 랜딩 Figma 포트 형태.
 * - 네이비(`--lp-navy`) 바탕 · 좌측 브랜드 로고 + 소개문 · 우측 링크 3열(리포트·고객 지원·입시 가이드) ·
 *   하단 사업자 정보(흰 글자 단일 톤, 원본은 명도 단계 없이 흰색 14px).
 * - 링크 컬럼·법적 링크는 config/footerLinks.ts SSoT — 워크스페이스 푸터(WorkspaceFooter)와 공유.
 * - 모바일(<md)은 링크 그룹을 **아코디언**(네이티브 <details>)으로 접는다. 두 마크업을 모두 렌더하고
 *   CSS(`md:hidden` / `hidden md:block`)로 가른다 — `useBreakpoint` 로 `:open` 을 바꾸면 SSG 프리렌더와
 *   데스크톱 hydration 이 어긋난다. 목록 렌더는 FooterLinkList 한 곳(링크 종류 판정 SSoT).
 */
const colLinkClass = 'text-[14px] text-white/90 transition-colors hover:text-lp-gold';
</script>

<template>
  <footer class="bg-lp-navy text-white">
    <div class="mx-auto max-w-page px-[18px] pb-10 pt-9 md:px-8 md:pt-10 xl:px-[60px]">
      <!-- 상단: 브랜드 + 링크 3열 -->
      <div class="grid grid-cols-1 gap-0 md:grid-cols-[1fr_auto] md:gap-10">
        <!-- 브랜드 -->
        <div class="pb-6 md:pb-0">
          <div class="mb-4">
            <BrandLogo variant="invert" />
          </div>
          <p class="m-0 max-w-[400px] text-[13px] leading-[22px] md:text-[14px] md:leading-6">
            30년간 대학 합격 점수를 매년 분석한
            <span class="whitespace-nowrap">입시 데이터 분석전문가</span>와
            <span class="whitespace-nowrap">대학 입학사정관 출신의</span>{{ ' ' }}
            <span class="whitespace-nowrap">대치동 입시 전문가</span>가
            <span class="whitespace-nowrap">전국 120여 개 대학의</span> 학과 및 전형별
            <span class="whitespace-nowrap">지원 전략과</span>{{ ' ' }}
            <span class="whitespace-nowrap">예상 합격선을</span> 직접 분석했습니다.
          </p>
          <!-- SNS — 네이버 블로그(채널 개설 전 빈 문자열이면 미렌더) -->
          <div v-if="brand.naverBlogUrl" class="mt-5">
            <a
              :href="brand.naverBlogUrl"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="네이버 블로그 바로가기"
              class="group inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 transition-colors duration-150 hover:bg-naver"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" class="h-9 w-9">
                <mask id="naver-blog-glyph">
                  <path
                    fill="white"
                    d="M5.5 5h13a3 3 0 0 1 3 3v5.5a3 3 0 0 1-3 3h-5.2l-1.6 2.4-1.6-2.4H5.5a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3Z"
                  />
                  <text
                    x="12"
                    y="10.75"
                    text-anchor="middle"
                    dominant-baseline="middle"
                    font-family="ui-sans-serif, system-ui, sans-serif"
                    font-size="7"
                    font-weight="800"
                    fill="black"
                  >
                    blog
                  </text>
                </mask>
                <rect width="24" height="24" mask="url(#naver-blog-glyph)" class="fill-white" />
              </svg>
            </a>
          </div>
        </div>

        <!-- 링크 컬럼(md+: 정적 3열) -->
        <div class="hidden gap-x-12 md:grid md:grid-cols-3 lg:gap-x-[46px]">
          <div v-for="col in FOOTER_COLUMNS" :key="`col-${col.title}`" class="min-w-[90px]">
            <h5 class="m-0 mb-[9px] text-[14px] font-extrabold leading-6 text-white">
              {{ col.title }}
            </h5>
            <FooterLinkList
              :links="col.links"
              item-class="mb-[5px] leading-6"
              :link-class="colLinkClass"
            />
          </div>
        </div>

        <!-- 링크 컬럼(<md: 아코디언) -->
        <div class="divide-y divide-white/10 border-y border-white/10 md:hidden">
          <details v-for="col in FOOTER_COLUMNS" :key="`acc-${col.title}`" class="foot-col">
            <summary class="flex cursor-pointer list-none items-center justify-between py-3.5">
              <h5 class="m-0 text-[14px] font-extrabold text-white">{{ col.title }}</h5>
              <ChevronDown
                class="foot-chev h-4 w-4 flex-none text-white/60 transition-transform"
                aria-hidden="true"
              />
            </summary>
            <FooterLinkList
              class="pb-2"
              :links="col.links"
              item-class="py-1.5"
              :link-class="`block ${colLinkClass}`"
            />
          </details>
        </div>
      </div>

      <!-- 사업자 정보 -->
      <div
        class="pt-6 text-[12.5px] font-medium leading-[22px] text-white/90 md:mt-10 md:pt-0 md:text-[14px] md:leading-6"
      >
        <p class="m-0">
          <b class="font-bold text-white">상호명</b> {{ brand.legal.company }}
          &nbsp;|&nbsp;
          <b class="font-bold text-white">대표자</b> {{ brand.legal.ceo }}
          &nbsp;|&nbsp;
          <b class="font-bold text-white">사업자등록번호</b> {{ brand.legal.bizNo }}<br />
          <b class="font-bold text-white">통신판매업번호</b>
          {{ brand.legal.mailOrderNo }} &nbsp;|&nbsp;
          <b class="font-bold text-white">출판사신고번호</b>
          {{ brand.legal.publisherNo }}<br />
          <b class="font-bold text-white">주소</b> {{ brand.legal.address }}
          &nbsp;|&nbsp;
          <b class="font-bold text-white">TEL</b> {{ brand.legal.tel }}
          &nbsp;|&nbsp;
          <b class="font-bold text-white">이메일</b> {{ brand.legal.email }}
        </p>
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <template v-for="(link, i) in FOOTER_LEGAL_LINKS" :key="link.to">
            <RouterLink
              :to="link.to"
              class="font-bold text-white transition-colors hover:text-lp-gold"
            >
              {{ link.label }}
            </RouterLink>
            <span v-if="i < FOOTER_LEGAL_LINKS.length - 1" aria-hidden="true">·</span>
          </template>
        </div>
        <div class="font-bold text-white">
          {{ brand.legal.copyright }}
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
/* summary 의 네이티브 디스클로저 마커 제거 */
.foot-col summary::-webkit-details-marker {
  display: none;
}

.foot-col[open] .foot-chev {
  transform: rotate(180deg);
}
</style>
